/**
 * The catalog and the material library as the agent's tools read them: held in memory, so a tool
 * can filter by tags and sizes. They used to be the static catalog shipped with the editor, which
 * moved to the catalog service. How the agent should read the service (an index of placeable
 * models; materials once they are published as recipes) is a question for the server side,
 * `docs/server-needs.md`. Until it is settled both load empty, and the agent's prompt says so.
 */
import type { ModelRef } from '../core/hp/types';
import type { CatalogItem as ServiceItem } from '../state/catalog';
import type { MaterialEntry } from '../state/materials';

export interface CatalogSection { id: string; title: string; photo?: string; count: number; ready: number }
/** A card the agent can place: the service's card, or a bare one without a model reference (test fixtures). */
export type CatalogItem = Omit<ServiceItem, 'catalogId' | 'modelRef'> & Partial<Pick<ServiceItem, 'catalogId' | 'modelRef'>>;
export interface Catalog { sections: CatalogSection[]; items: CatalogItem[] }

export const loadAgentCatalog = (): Promise<Catalog> => Promise.resolve({ sections: [], items: [] });
export const loadAgentMaterials = (): Promise<MaterialEntry[]> => Promise.resolve([]);

export function searchCatalog(catalog: Catalog, query: string): CatalogItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return catalog.items.filter((i) =>
    i.title.toLowerCase().includes(q)
    || i.id.toLowerCase().includes(q)
    || i.tags.some((t) => t.toLowerCase().includes(q)));
}

/** What an item placed from `item` refers to: the service's own reference, or a built-in path. */
export const modelRefOf = (item: CatalogItem): ModelRef =>
  item.modelRef ? { ...item.modelRef } : { kind: 'local', id: item.id, raw: 'Models/' + item.id };
